/**
 * HUB-F — Onglet Réparations du Centre Activité.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import useActiviteReparations from '../../../hooks/activite/useActiviteReparations';
import ActiviteReparationsAccessBanner from '../ActiviteReparationsAccessBanner';
import ActiviteReparationsKpiStrip from '../ActiviteReparationsKpiStrip';
import ActiviteReparationsSectionTabs from '../ActiviteReparationsSectionTabs';
import ActiviteReparationCard from '../ActiviteReparationCard';
import { ACTIVITE_TAB_IDS } from '../../../utils/activite/activityTabs';
import { REPARATION_SECTIONS } from '../../../utils/reparations/reparationsDisplay';

function RepairList({ items, loading, emptyMessage, testId }) {
  if (loading) {
    return <div className="flex justify-center py-12"><div className="loading-spinner" /></div>;
  }
  if (!items.length) {
    return (
      <p className="text-center py-12 text-[var(--rm-gray-500)]" data-testid={testId}>
        {emptyMessage}
      </p>
    );
  }
  return (
    <div className="space-y-3" data-testid={`${testId}-list`}>
      {items.map((d) => (
        <ActiviteReparationCard key={d.ref || d.id} device={d} />
      ))}
    </div>
  );
}

export default function ActiviteReparationsTab({ filters }) {
  const { api } = useAuth();
  const [activeSection, setActiveSection] = useState(REPARATION_SECTIONS.OVERVIEW);

  const {
    recent,
    enCours,
    attentePiece,
    termine,
    devices,
    magasinCounts,
    kpis,
    loading,
    error,
  } = useActiviteReparations(api, filters);

  const sectionCounts = useMemo(() => ({
    [REPARATION_SECTIONS.OVERVIEW]: recent.length,
    [REPARATION_SECTIONS.EN_COURS]: enCours.length,
    [REPARATION_SECTIONS.ATTENTE_PIECE]: attentePiece.length,
    [REPARATION_SECTIONS.TERMINE]: termine.length,
    [REPARATION_SECTIONS.RECENT]: devices.length,
  }), [recent, enCours, attentePiece, termine, devices]);

  const renderOverview = () => (
    <div className="space-y-4">
      {(enCours.length > 0 || attentePiece.length > 0 || termine.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="activite-reparations-overview-highlights">
          {enCours.length > 0 && (
            <button
              type="button"
              onClick={() => setActiveSection(REPARATION_SECTIONS.EN_COURS)}
              className="rounded-xl border p-3 text-left min-h-[44px]"
              style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}
              data-testid="activite-reparations-highlight-en-cours"
            >
              <div className="text-xs text-[var(--rm-gray-500)]">En cours</div>
              <div className="font-bold text-[var(--rm-white)]">{enCours.length}</div>
            </button>
          )}
          {attentePiece.length > 0 && (
            <button
              type="button"
              onClick={() => setActiveSection(REPARATION_SECTIONS.ATTENTE_PIECE)}
              className="rounded-xl border p-3 text-left min-h-[44px]"
              style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}
              data-testid="activite-reparations-highlight-attente"
            >
              <div className="text-xs text-[var(--rm-gray-500)]">Attente pièce</div>
              <div className="font-bold text-[var(--rm-gray-200)]">{attentePiece.length}</div>
            </button>
          )}
          {termine.length > 0 && (
            <button
              type="button"
              onClick={() => setActiveSection(REPARATION_SECTIONS.TERMINE)}
              className="rounded-xl border p-3 text-left min-h-[44px]"
              style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}
              data-testid="activite-reparations-highlight-termine"
            >
              <div className="text-xs text-[var(--rm-gray-500)]">Prêtes / terminées</div>
              <div className="font-bold text-[var(--rm-success)]">{termine.length}</div>
            </button>
          )}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <h3 className="text-sm font-semibold text-[var(--rm-white)]">20 réparations récentes</h3>
          <Link
            to="/reparations"
            className="text-xs text-[var(--rm-gray-400)] hover:text-[var(--rm-white)] inline-flex items-center gap-1 min-h-[44px] px-2"
            data-testid="activite-reparations-link-all"
          >
            Toutes les réparations
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <RepairList
          items={recent}
          loading={loading}
          emptyMessage="Aucune réparation sur la période"
          testId="activite-reparations-recent"
        />
      </div>
    </div>
  );

  const renderSection = () => {
    switch (activeSection) {
      case REPARATION_SECTIONS.EN_COURS:
        return (
          <RepairList
            items={enCours}
            loading={loading}
            emptyMessage="Aucune réparation en cours"
            testId="activite-reparations-en-cours"
          />
        );
      case REPARATION_SECTIONS.ATTENTE_PIECE:
        return (
          <RepairList
            items={attentePiece}
            loading={loading}
            emptyMessage="Aucune réparation en attente de pièce"
            testId="activite-reparations-attente-piece"
          />
        );
      case REPARATION_SECTIONS.TERMINE:
        return (
          <div className="space-y-3">
            <p className="text-xs text-[var(--rm-gray-500)]" data-testid="activite-reparations-termine-hint">
              Réparations terminées, prêtes à être récupérées par le client.
            </p>
            <RepairList
              items={termine}
              loading={loading}
              emptyMessage="Aucune réparation terminée"
              testId="activite-reparations-termine"
            />
          </div>
        );
      case REPARATION_SECTIONS.RECENT:
        return (
          <RepairList
            items={devices.slice(0, 50)}
            loading={loading}
            emptyMessage="Aucune réparation"
            testId="activite-reparations-all"
          />
        );
      default:
        return renderOverview();
    }
  };

  return (
    <div
      className="rounded-xl border p-4 sm:p-5 space-y-4"
      style={{ background: 'var(--rm-bg-elevated)', borderColor: 'var(--rm-border-subtle)' }}
      data-testid="activite-reparations-tab"
    >
      <ActiviteReparationsAccessBanner />

      <ActiviteReparationsKpiStrip
        kpis={kpis}
        magasinId={filters.magasin_id}
        magasinCounts={magasinCounts}
      />

      <ActiviteReparationsSectionTabs
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        counts={sectionCounts}
      />

      {error && (
        <div
          className="rounded-xl border p-4 text-sm text-[var(--rm-red-light)]"
          style={{ borderColor: 'var(--rm-border-strong)', background: 'var(--rm-red-muted)' }}
          data-testid="activite-reparations-error"
        >
          {error}
        </div>
      )}

      {renderSection()}

      <span className="sr-only" data-testid="activite-reparations-tab-id">{ACTIVITE_TAB_IDS.REPARATIONS}</span>
    </div>
  );
}
